/**
 * Personuppgiftsbilaga till hyresavtal — GDPR artikel 13 information.
 *
 * The same approved document is issued two ways: as a bilaga riding its
 * parent lease's signing (`personuppgiftsbilaga`) and as a standalone,
 * signed `personuppgiftsavtal`. Sections 1–9 are shared verbatim; only the
 * slot annotations of § 1 (derived from the parent lease vs autofilled from
 * the lessor party) and the closing § 10 differ.
 *
 * Legal text verbatim from `base-templates/Personuppgiftsbilaga till
 * hyresavtal.docx` — do not paraphrase or reorder.
 */
import { nodes, p, section, t } from '@hyranai/regta-document-ir';
import { autoClause, gdprPartySlot, gdprRows, legalBase, staticParagraph } from './text.js';
export const SOURCE = 'base-templates/Personuppgiftsbilaga till hyresavtal.docx';
export const BEKRAFTELSE_TEXT = 'Hyresgästen bekräftar genom sin underskrift att information om personuppgiftsbehandling har mottagits.';
export const PREAMBLE = 'Information om hyresvärdens behandling av hyresgästens personuppgifter.';
/**
 * The landlord identity is overlaid from the parent lease on the bilaga and
 * autofilled from the `(lessor, 0)` party on the standalone. E-post/telefon
 * stay plain on the bilaga because not every parent lease carries them.
 */
const landlordSlots = (variant) => [
    gdprPartySlot(variant, { key: 'landlord_name', label: 'Hyresvärd', role: 'lessor', field: 'name', required: true }),
    gdprPartySlot(variant, { key: 'landlord_org_number', label: 'Organisationsnummer', role: 'lessor', field: 'legalId', required: true }),
    gdprPartySlot(variant, { key: 'landlord_address', label: 'Adress', role: 'lessor', field: 'address', required: true }),
    gdprPartySlot(variant, { key: 'landlord_email', label: 'E-post', role: 'lessor', field: 'email', fromParent: false }),
    gdprPartySlot(variant, { key: 'landlord_phone', label: 'Telefon', role: 'lessor', field: 'phone', fromParent: false }),
];
export function personuppgiftsansvarigBlock(blockKey, variant) {
    return {
        ...legalBase(SOURCE),
        blockKey,
        version: 'v1',
        title: 'Personuppgiftsansvarig',
        summary: 'Identifies the landlord as data controller: name, organisation number, address and contact details.',
        slots: landlordSlots(variant),
        compile: (input) => [
            section('personuppgiftsansvarig', 'Personuppgiftsansvarig', nodes(p('Personuppgiftsansvarig för behandlingen av personuppgifter är:'), gdprRows(input, [
                ['Hyresvärd', 'landlord_name'],
                ['Organisationsnummer', 'landlord_org_number'],
                ['Adress', 'landlord_address'],
                ['E-post', 'landlord_email'],
                ['Telefon', 'landlord_phone'],
            ]))),
        ],
    };
}
export const intro = staticParagraph({
    blockKey: 'personuppgiftsbilaga.intro',
    title: 'Inledning',
    summary: 'States that the document is the information required by GDPR article 13.',
    source: SOURCE,
    text: 'Denna bilaga utgör information enligt artikel 13 i Europaparlamentets och rådets förordning (EU) 2016/679 (GDPR).',
});
export const personuppgiftsansvarig = personuppgiftsansvarigBlock('personuppgiftsbilaga.personuppgiftsansvarig', 'bilaga');
export const andamal = autoClause({
    blockKey: 'personuppgiftsbilaga.andamal',
    ref: 'andamal',
    title: 'Ändamål med behandlingen',
    summary: 'The purposes for which the landlord processes the tenant’s personal data.',
    source: SOURCE,
    body: [
        'Hyresvärden behandlar personuppgifter för att:',
        {
            bullets: [
                'administrera hyresförhållandet,',
                'fullgöra hyresavtalet,',
                'hantera hyresbetalningar,',
                'kommunicera med hyresgästen,',
                'utföra felanmälningar och serviceärenden,',
                'uppfylla rättsliga skyldigheter,',
                'hantera tvister och rättsliga anspråk,',
                'bedriva fastighetsförvaltning och säkerhetsarbete.',
            ],
        },
    ],
});
export const kategorier = autoClause({
    blockKey: 'personuppgiftsbilaga.kategorier',
    ref: 'kategorier',
    title: 'Kategorier av personuppgifter',
    summary: 'The categories of personal data the landlord may process.',
    source: SOURCE,
    body: [
        'Hyresvärden kan behandla följande uppgifter:',
        {
            bullets: [
                'namn,',
                'personnummer eller samordningsnummer,',
                'kontaktuppgifter,',
                'adressuppgifter,',
                'betalningsuppgifter,',
                'uppgifter om hyresförhållandet,',
                'uppgifter som lämnas i samband med felanmälningar eller andra kontakter med hyresvärden.',
            ],
        },
    ],
});
export const rattsligGrund = autoClause({
    blockKey: 'personuppgiftsbilaga.rattslig-grund',
    ref: 'rattslig-grund',
    title: 'Rättslig grund',
    summary: 'The legal bases for the processing.',
    source: SOURCE,
    body: [
        'Behandlingen sker med stöd av:',
        {
            bullets: [
                'fullgörande av avtal,',
                'rättslig förpliktelse,',
                'berättigat intresse, samt, när så krävs enligt lag, samtycke från hyresgästen.',
            ],
        },
    ],
});
export const mottagare = autoClause({
    blockKey: 'personuppgiftsbilaga.mottagare',
    ref: 'mottagare',
    title: 'Mottagare av personuppgifter',
    summary: 'Who the personal data may be disclosed to; no disclosure for marketing without specific support.',
    source: SOURCE,
    body: [
        'Personuppgifter kan komma att lämnas ut till:',
        {
            bullets: [
                'fastighetsförvaltare,',
                'tekniska entreprenörer,',
                'IT-leverantörer,',
                'betalningsförmedlare,',
                'inkassobolag,',
                'myndigheter när lag eller myndighetsbeslut kräver detta.',
            ],
        },
        'Personuppgifter lämnas inte ut till tredje man för marknadsföringsändamål utan särskilt stöd.',
    ],
});
export const lagringstid = autoClause({
    blockKey: 'personuppgiftsbilaga.lagringstid',
    ref: 'lagringstid',
    title: 'Lagringstid',
    summary: 'How long the personal data is retained.',
    source: SOURCE,
    body: [
        'Personuppgifter sparas så länge det är nödvändigt för att administrera hyresförhållandet och därefter under den tid som krävs enligt lag eller för att fastställa, göra gällande eller försvara rättsliga anspråk.',
    ],
});
export const rattigheter = autoClause({
    blockKey: 'personuppgiftsbilaga.rattigheter',
    ref: 'rattigheter',
    title: 'Hyresgästens rättigheter',
    summary: 'The tenant’s data-subject rights.',
    source: SOURCE,
    body: [
        'Hyresgästen har rätt att:',
        {
            bullets: [
                'begära registerutdrag,',
                'begära rättelse,',
                'begära radering när förutsättningar finns,',
                'begära begränsning av behandling,',
                'invända mot viss behandling,',
                'begära dataportabilitet när lagens förutsättningar är uppfyllda.',
            ],
        },
    ],
});
export const klagomal = autoClause({
    blockKey: 'personuppgiftsbilaga.klagomal',
    ref: 'klagomal',
    title: 'Klagomål',
    summary: 'The tenant’s right to complain to the Swedish data protection authority (IMY).',
    source: SOURCE,
    body: ['Hyresgästen har rätt att lämna klagomål till Integritetsskyddsmyndigheten (IMY).'],
});
export const kontakt = {
    ...legalBase(SOURCE),
    blockKey: 'personuppgiftsbilaga.kontakt',
    version: 'v1',
    title: 'Kontakt',
    summary: 'Who at the landlord answers questions about the processing.',
    slots: [
        {
            key: 'contact_person',
            label: 'Kontaktperson',
            type: 'text',
            help: 'Person hos hyresvärden som hanterar frågor om personuppgiftsbehandlingen.',
        },
        { key: 'contact_phone', label: 'Telefon', type: 'text' },
        { key: 'contact_email', label: 'E-post', type: 'text' },
    ],
    compile: (input) => [
        section('kontakt', 'Kontakt', nodes(p('Frågor om personuppgiftsbehandlingen kan ställas till:'), gdprRows(input, [
            ['Kontaktperson', 'contact_person'],
            ['Telefon', 'contact_phone'],
            ['E-post', 'contact_email'],
        ]))),
    ],
};
/**
 * § 10 on the bilaga: the confirmation sentence alone, plus the note that the
 * bilaga is signed together with its parent lease.
 */
export const bekraftelse = {
    ...legalBase(SOURCE),
    blockKey: 'personuppgiftsbilaga.bekraftelse',
    version: 'v1',
    title: 'Bekräftelse',
    summary: 'The tenant’s confirmation of receipt; signed as part of the parent lease, not on this bilaga.',
    slots: [],
    compile: () => [
        section('bekraftelse', 'Bekräftelse', [
            p(BEKRAFTELSE_TEXT),
            p([
                t('Denna personuppgiftsbilaga utgör en del av hyresavtalet och undertecknas elektroniskt tillsammans med det. Egen underskrift krävs inte på denna handling.'),
            ], { variant: 'fine_print' }),
        ]),
    ],
};
/** Sections 2–8 in document order — shared verbatim by both variants. */
export const STATIC_SECTIONS = [andamal, kategorier, rattsligGrund, mottagare, lagringstid, rattigheter, klagomal];
export const PERSONUPPGIFTSBILAGA_BLOCKS = [intro, personuppgiftsansvarig, ...STATIC_SECTIONS, kontakt, bekraftelse];
//# sourceMappingURL=blocks.js.map